// ============================================================================
// Single data + logic layer for the universal Turnover Task modal (create + edit)
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO, setHours, setMinutes, setSeconds, isWithinInterval } from "date-fns";
import { parseLocalDateOnly, toLocalCalendarDate } from "@/lib/dateUtils";
import { useUpdateCleaningTask, useDeleteCleaningTask } from "@/hooks/useEditCleaningTask";
import { useCreateSingleTask, useCreateManualTask } from "@/hooks/useCleaningTasks";
import { usePropertyAssignableMembers, USER_PREFIX } from "@/hooks/usePropertyAssignableMembers";
import { useChecklists } from "@/hooks/useChecklists";
import { usePropertyPreventativeMaintenance } from "@/hooks/usePreventativeMaintenance";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { usePropertyCleaningAssignment } from "@/hooks/useCleaningCompanies";
import { useAllCompanies } from "@/hooks/useCleaningCompanies";
import { useAuth } from "@/contexts/AuthContext";
import type { CleaningTaskWithRelations } from "@/types/cleaning";
import type { TurnoverTaskModalMode } from "@/contexts/TurnoverTaskModalContext";

const DEFAULT_START = "10:00";
const DEFAULT_DURATION_MINUTES = 120;
const DEFAULT_CHECK_IN = "4:00 PM";
const DEFAULT_CHECK_OUT = "11:00 AM";

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(min: number): string {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

/** Reservation from API (may include check_in_time, check_out_time) */
interface ReservationRow {
  id: string;
  start_date: string;
  end_date: string;
  check_in_time?: string | null;
  check_out_time?: string | null;
  guest_count?: number | null;
}

export interface UseTurnoverTaskFormParams {
  mode: TurnoverTaskModalMode;
  propertyId: string;
  taskId?: string;
  reservationId?: string;
  initialDate?: Date;
}

export function useTurnoverTaskForm({
  mode,
  propertyId,
  taskId,
  reservationId,
  initialDate,
}: UseTurnoverTaskFormParams) {
  const { user } = useAuth();
  const { data: property } = usePropertyWithAccess(propertyId || undefined);
  const { data: checklists = [] } = useChecklists(propertyId || undefined);
  const { data: propertyPpm = [] } = usePropertyPreventativeMaintenance(propertyId || undefined);
  const { members: assignableMembers } = usePropertyAssignableMembers(propertyId || undefined);
  const { data: companyAssignment } = usePropertyCleaningAssignment(propertyId || undefined);
  const { data: allCompanies } = useAllCompanies();

  /** Team members (assignable as executor: "Name (Team)"). */
  const teamMembers = useMemo(
    () => assignableMembers.filter((m) => m.role === "Team admin"),
    [assignableMembers]
  );
  /** Cleaners (assignable as executor: "Name (Cleaner)"). */
  const cleanerMembers = useMemo(
    () => assignableMembers.filter((m) => m.role === "Cleaner"),
    [assignableMembers]
  );
  /** Optional coordinator (legacy: team member who manages but does not execute). */
  const coordinatorOptions = useMemo(() => teamMembers, [teamMembers]);

  const { data: task, isLoading: taskLoading } = useQuery({
    queryKey: ["cleaning-task", taskId],
    queryFn: async () => {
      if (!taskId) return null;
      const { data, error } = await supabase
        .from("cleaning_tasks")
        .select("*")
        .eq("id", taskId)
        .single();
      if (error || !data) return null;
      const taskRow = data as { checklist_id?: string | null; include_inventory_check?: boolean } & Record<string, unknown>;
      let checklists: { id: string; name: string } | null = null;
      if (taskRow.checklist_id) {
        const { data: checklistRow } = await supabase
          .from("checklists")
          .select("id, name")
          .eq("id", taskRow.checklist_id)
          .maybeSingle();
        if (checklistRow) checklists = checklistRow;
      }
      const { data: assignmentRows } = await supabase
        .from("cleaning_task_checklist_assignments")
        .select("checklist_id, assigned_type, cleaner_id, assignee_user_id")
        .eq("cleaning_task_id", taskId);
      return { ...taskRow, checklists, assignmentRows: assignmentRows ?? [] } as unknown as CleaningTaskWithRelations & {
        reservations?: ReservationRow | null;
        include_inventory_check?: boolean;
        assignmentRows?: Array<{ checklist_id: string; assigned_type: string; cleaner_id: string | null; assignee_user_id: string | null }>;
      };
    },
    enabled: mode === "edit" && !!taskId,
  });

  const effectiveReservationId = taskId && task?.reservation_id
    ? task.reservation_id
    : reservationId;

  const { data: reservation, isLoading: reservationLoading } = useQuery({
    queryKey: ["reservation", effectiveReservationId],
    queryFn: async () => {
      if (!effectiveReservationId) return null;
      const { data, error } = await supabase
        .from("reservations")
        .select("id, start_date, end_date, check_in_time, check_out_time, guest_count")
        .eq("id", effectiveReservationId)
        .single();
      if (error || !data) return null;
      return data as ReservationRow;
    },
    enabled: !!effectiveReservationId,
  });

  const reservationData = reservation ?? null;

  const updateTask = useUpdateCleaningTask();
  const deleteTask = useDeleteCleaningTask();
  const createSingleTask = useCreateSingleTask();
  const createManualTask = useCreateManualTask();

  const [assigneeValue, setAssigneeValue] = useState<string>("unassigned");
  const [coordinatorValue, setCoordinatorValue] = useState<string>("");
  const [checklistId, setChecklistId] = useState<string | null>(null);
  /** Multi-checklist: each row is { checklistId, assigneeValue } where assigneeValue is "self" | USER_PREFIX+userId | cleanerId | "unassigned". */
  const [checklistAssignments, setChecklistAssignments] = useState<Array<{ checklistId: string; assigneeValue: string }>>([]);
  const [includeInventoryCheck, setIncludeInventoryCheck] = useState<boolean>(true);
  const [scheduledDate, setScheduledDateRaw] = useState<Date | undefined>(undefined);
  const setScheduledDate = useCallback((d: Date | undefined) => {
    setScheduledDateRaw(d ? toLocalCalendarDate(d) : undefined);
  }, []);
  const [startTime, setStartTime] = useState(DEFAULT_START);
  const [endTime, setEndTime] = useState(minutesToTime(timeToMinutes(DEFAULT_START) + DEFAULT_DURATION_MINUTES));
  const [status, setStatus] = useState<string>("scheduled");
  const [notes, setNotes] = useState("");
  const [overrideCheckInTime, setOverrideCheckInTime] = useState("");
  const [overrideCheckOutTime, setOverrideCheckOutTime] = useState("");
  const [guestCount, setGuestCount] = useState("");

  // Exclude checklists that are used for preventative maintenance (so they don't appear in create turnover task dropdown)
  const propertyChecklists = useMemo(() => {
    const pmChecklistIds = new Set(
      (propertyPpm ?? []).map((p) => p.checklist_id).filter(Boolean) as string[]
    );
    return (Array.isArray(checklists) ? checklists : [])
      .filter((c) => !pmChecklistIds.has(c.id))
      .map((c) => ({ id: c.id, name: c.name }));
  }, [checklists, propertyPpm]);

  const defaultChecklistId =
    property?.default_checklist_id ??
    (propertyChecklists.length > 0 ? propertyChecklists[0].id : null);

  // Initialize form from task (edit) or defaults (create)
  useEffect(() => {
    if (mode === "edit" && task) {
      const t = task as CleaningTaskWithRelations & {
        assignee_user_id?: string | null;
        coordinator_user_id?: string | null;
        include_inventory_check?: boolean;
        assignmentRows?: Array<{ checklist_id: string; assigned_type: string; cleaner_id: string | null; assignee_user_id: string | null }>;
      };
      if (t.coordinator_user_id) setCoordinatorValue(USER_PREFIX + t.coordinator_user_id);
      else setCoordinatorValue("");
      setIncludeInventoryCheck(t.include_inventory_check !== false);
      if (t.assignmentRows && t.assignmentRows.length > 0) {
        setChecklistAssignments(
          t.assignmentRows.map((row) => {
            let av = "unassigned";
            if (row.assigned_type === "owner" && user?.id === row.assignee_user_id) av = "self";
            else if (row.assignee_user_id) av = USER_PREFIX + row.assignee_user_id;
            else if (row.cleaner_id) av = row.cleaner_id;
            return { checklistId: row.checklist_id, assigneeValue: av };
          })
        );
        setChecklistId(t.assignmentRows[0].checklist_id);
        setAssigneeValue("unassigned");
      } else {
        const assignedType = t.assigned_type;
        let av = "unassigned";
        if (assignedType === "owner" || (t.assignee_user_id && user?.id === t.assignee_user_id)) av = "self";
        else if (t.assignee_user_id) av = USER_PREFIX + t.assignee_user_id;
        else if (task.cleaner_id) av = task.cleaner_id;
        setAssigneeValue(av);
        setChecklistId(task.checklist_id ?? null);
        setChecklistAssignments(task.checklist_id ? [{ checklistId: task.checklist_id, assigneeValue: av }] : []);
      }
      // Fallback to today so Save is never disabled when task exists but has no date
      setScheduledDateRaw(
        task.scheduled_date
          ? parseLocalDateOnly(task.scheduled_date)
          : reservationData?.end_date
            ? parseLocalDateOnly(reservationData.end_date)
            : new Date()
      );
      setNotes((task as { notes?: string | null }).notes || "");
      setStatus(task.status);
      setOverrideCheckInTime(task.override_check_in_time || "");
      setOverrideCheckOutTime(task.override_check_out_time || "");
      if (task.scheduled_start_at && task.scheduled_end_at) {
        const start = parseISO(task.scheduled_start_at);
        const end = parseISO(task.scheduled_end_at);
        setStartTime(format(start, "HH:mm"));
        setEndTime(format(end, "HH:mm"));
      } else {
        setStartTime(DEFAULT_START);
        setEndTime(minutesToTime(timeToMinutes(DEFAULT_START) + DEFAULT_DURATION_MINUTES));
      }
      return;
    }

    if (mode === "create") {
      setAssigneeValue("unassigned");
      setCoordinatorValue("");
      setStatus("scheduled");
      setChecklistId(defaultChecklistId);
      setChecklistAssignments(defaultChecklistId ? [{ checklistId: defaultChecklistId, assigneeValue: "unassigned" }] : []);
      setIncludeInventoryCheck(true);

      const defaultDate = reservationData?.end_date
        ? parseLocalDateOnly(reservationData.end_date)
        : initialDate
          ? toLocalCalendarDate(initialDate)
          : new Date();
      setScheduledDateRaw(defaultDate);

      const checkoutDisplay = reservationData?.check_out_time || DEFAULT_CHECK_OUT;
      const hasOverride = false;
      const startStr = hasOverride ? DEFAULT_START : parseTimeTo24(checkoutDisplay) ?? DEFAULT_START;
      const startM = timeToMinutes(startStr);
      const endM = Math.min(startM + DEFAULT_DURATION_MINUTES, 24 * 60 - 30);
      setStartTime(startStr);
      setEndTime(minutesToTime(endM));

      setOverrideCheckInTime(reservationData?.check_in_time || "");
      setOverrideCheckOutTime(reservationData?.check_out_time || "");
      setNotes("");
    }
  }, [mode, task, reservationData, initialDate, defaultChecklistId]);

  // Guest count: reservation wins when linked; otherwise cleaning_tasks.guest_count (manual tasks).
  useEffect(() => {
    if (mode === "edit" && task && !taskLoading) {
      const resId = (task as { reservation_id?: string | null }).reservation_id;
      if (resId && reservationLoading) return;
      const fromRes = reservationData?.guest_count;
      const fromTask = (task as { guest_count?: number | null }).guest_count;
      const v =
        resId && reservationData != null
          ? fromRes != null && fromRes > 0
            ? fromRes
            : null
          : fromTask != null && fromTask > 0
            ? fromTask
            : null;
      setGuestCount(v != null ? String(v) : "");
      return;
    }
    if (mode === "create") {
      if (effectiveReservationId && reservationLoading) return;
      const gc = reservationData?.guest_count;
      setGuestCount(gc != null && gc > 0 ? String(gc) : "");
    }
  }, [
    mode,
    task,
    taskLoading,
    reservationLoading,
    reservationData?.guest_count,
    reservationData?.id,
    effectiveReservationId,
  ]);

  const isSaving = updateTask.isPending || createSingleTask.isPending || createManualTask.isPending;
  const isDeleting = deleteTask.isPending;

  const save = async () => {
    if (!scheduledDate) return;

    const range = reservationData?.start_date && reservationData?.end_date
      ? { start: parseLocalDateOnly(reservationData.start_date), end: parseLocalDateOnly(reservationData.end_date) }
      : null;
    if (range && !isWithinInterval(scheduledDate, { start: range.start, end: range.end })) {
      throw new Error("Cleaning date must be within the reservation dates.");
    }

    const dateStr = format(scheduledDate, "yyyy-MM-dd");
    const startM = timeToMinutes(startTime);
    const endM = timeToMinutes(endTime);
    if (endM <= startM) throw new Error("End time must be after start time");

    const parseGuestCountInput = (s: string): number | null => {
      const trimmed = s.trim();
      if (!trimmed) return null;
      const n = parseInt(trimmed, 10);
      if (Number.isNaN(n) || n < 1 || n > 99) {
        throw new Error("Expected guests must be a whole number from 1 to 99, or leave blank.");
      }
      return n;
    };
    const parsedGuestCount = parseGuestCountInput(guestCount);

    const [startH, startMin] = startTime.split(":").map(Number);
    const [endH, endMin] = endTime.split(":").map(Number);
    const startAt = setSeconds(setMinutes(setHours(new Date(scheduledDate), startH), startMin), 0);
    const endAt = setSeconds(setMinutes(setHours(new Date(scheduledDate), endH), endMin), 0);
    const scheduledStartAt = startAt.toISOString();
    const scheduledEndAt = endAt.toISOString();

    const coordinatorUserId =
      coordinatorValue.startsWith(USER_PREFIX) ? coordinatorValue.slice(USER_PREFIX.length) : null;

    const useMultiAssignments = checklistAssignments.length > 0;
    const assignmentsPayload = useMultiAssignments
      ? checklistAssignments
          .filter((row) => row.assigneeValue !== "unassigned")
          .map((row) => {
            const isSelf = row.assigneeValue === "self";
            const isTeamUser = row.assigneeValue.startsWith(USER_PREFIX);
            const at: "owner" | "team" | "cleaner" = isSelf ? "owner" : isTeamUser ? "team" : "cleaner";
            const au = isSelf ? (user?.id ?? null) : isTeamUser ? row.assigneeValue.slice(USER_PREFIX.length) : null;
            const cid = at === "cleaner" ? row.assigneeValue : null;
            return { checklistId: row.checklistId, assignedType: at, cleanerId: cid, assigneeUserId: au };
          })
      : undefined;
    const hasValidAssignments = assignmentsPayload && assignmentsPayload.length > 0;
    if (useMultiAssignments && !hasValidAssignments) {
      throw new Error("Assign at least one checklist to a person.");
    }

    const assertTeamCanExecute = (uid: string | null) => {
      if (!uid) return;
      const teamMember = teamMembers.find((m) => m.userId === uid);
      if (teamMember && (teamMember as { can_execute_tasks?: boolean }).can_execute_tasks === false) {
        throw new Error("This team member can't do tasks. Enable 'Can do tasks' in Settings → Access & Team, or assign to someone else.");
      }
    };

    if (useMultiAssignments) {
      if (assignmentsPayload!.some((a) => a.assignedType === "owner" && !a.assigneeUserId)) {
        throw new Error("Owner assignment requires your user account.");
      }
      if (assignmentsPayload!.some((a) => a.assignedType === "team" && !a.assigneeUserId)) {
        throw new Error("Team assignment requires a team member.");
      }
      assignmentsPayload!.filter((a) => a.assignedType === "team").forEach((a) => assertTeamCanExecute(a.assigneeUserId ?? null));
      if (assignmentsPayload!.some((a) => a.assignedType === "cleaner" && !a.cleanerId)) {
        throw new Error("Cleaner assignment requires a cleaner selected.");
      }
    } else {
      const isSelf = assigneeValue === "self";
      const isTeamUser = assigneeValue.startsWith(USER_PREFIX);
      const assigneeUserId = isSelf ? (user?.id ?? null) : isTeamUser ? assigneeValue.slice(USER_PREFIX.length) : null;
      const cleanerId = assigneeValue === "unassigned" || isSelf || isTeamUser ? null : assigneeValue;
      const assignedType: "owner" | "cleaner" | "unassigned" | "team" = isSelf ? "owner" : assigneeUserId ? "team" : cleanerId ? "cleaner" : "unassigned";
      if (assignedType === "owner" && !assigneeUserId) throw new Error("Owner assignment requires your user account.");
      if (assignedType === "team" && !assigneeUserId) throw new Error("Team assignment requires a team member.");
      if (assignedType === "team") assertTeamCanExecute(assigneeUserId);
      if (assignedType === "cleaner" && !cleanerId) throw new Error("Cleaner assignment requires a cleaner selected.");
    }

    const legacyCleanerId = assigneeValue === "self" || assigneeValue.startsWith(USER_PREFIX) ? null : (assigneeValue === "unassigned" ? null : assigneeValue);
    const legacyAssigneeUserId = assigneeValue === "self" ? (user?.id ?? null) : assigneeValue.startsWith(USER_PREFIX) ? assigneeValue.slice(USER_PREFIX.length) : null;
    const legacyAssignedType: "owner" | "cleaner" | "unassigned" | "team" = assigneeValue === "self" ? "owner" : legacyAssigneeUserId ? "team" : legacyCleanerId ? "cleaner" : "unassigned";

    // Keep in-progress handoffs on the same visit — never demote status on save.
    const persistStatus =
      mode === "edit" && task && (task.started_at || task.status === "in_progress") && status === "scheduled"
        ? "in_progress"
        : status;

    if (
      mode === "edit" &&
      task &&
      (task.started_at || task.status === "in_progress") &&
      legacyAssignedType === "unassigned" &&
      !hasValidAssignments
    ) {
      throw new Error(
        "This job is already in progress. Pass it to another person instead of leaving it unassigned."
      );
    }

    if (mode === "edit" && taskId) {
      await updateTask.mutateAsync({
        taskId,
        propertyId,
        cleanerId: legacyCleanerId,
        assigneeUserId: legacyAssigneeUserId,
        assignedType: legacyAssignedType,
        coordinatorUserId: coordinatorUserId ?? null,
        checklistId: checklistAssignments[0]?.checklistId ?? checklistId,
        assignments: hasValidAssignments ? assignmentsPayload : undefined,
        includeInventoryCheck,
        scheduledDate: dateStr,
        notes: notes.trim() || null,
        status: persistStatus,
        overrideCheckInTime: overrideCheckInTime.trim() || null,
        overrideCheckOutTime: overrideCheckOutTime.trim() || null,
        scheduledStartAt,
        scheduledEndAt,
        guestCount: effectiveReservationId ? undefined : parsedGuestCount,
      });
      if (effectiveReservationId) {
        const { error: resGuestErr } = await supabase
          .from("reservations")
          .update({ guest_count: parsedGuestCount })
          .eq("id", effectiveReservationId);
        if (resGuestErr) throw resGuestErr;
      }
      return;
    }

    if (mode === "create") {
      if (effectiveReservationId) {
        await supabase
          .from("reservations")
          .update({
            check_out_time: overrideCheckOutTime.trim() || null,
            check_in_time: overrideCheckInTime.trim() || null,
            guest_count: parsedGuestCount,
          })
          .eq("id", effectiveReservationId);

        await createSingleTask.mutateAsync({
          propertyId,
          reservationId: effectiveReservationId,
          checklistId: checklistAssignments[0]?.checklistId ?? checklistId,
          cleanerId: legacyCleanerId,
          assigneeUserId: legacyAssigneeUserId,
          assignedType: legacyAssignedType,
          assignments: hasValidAssignments ? assignmentsPayload : undefined,
          includeInventoryCheck,
          coordinatorUserId: coordinatorUserId ?? null,
          scheduledDate: dateStr,
          scheduledStartAt,
          scheduledEndAt,
          overrideCheckOutTime: overrideCheckOutTime.trim() || null,
          overrideCheckInTime: overrideCheckInTime.trim() || null,
          notes: notes.trim() || null,
        });
      } else {
        await createManualTask.mutateAsync({
          propertyId,
          scheduledDate: dateStr,
          scheduledStartAt,
          scheduledEndAt,
          checklistId: checklistAssignments[0]?.checklistId ?? checklistId,
          cleanerId: legacyCleanerId,
          assigneeUserId: legacyAssigneeUserId,
          assignedType: legacyAssignedType,
          assignments: hasValidAssignments ? assignmentsPayload : undefined,
          includeInventoryCheck,
          coordinatorUserId: coordinatorUserId ?? null,
          notes: notes.trim() || null,
          guestCount: parsedGuestCount,
        });
      }
    }
  };

  const remove = async () => {
    if (mode !== "edit" || !taskId) return;
    await deleteTask.mutateAsync({ taskId, propertyId });
  };

  const reservationDisplayCheckIn = reservationData?.check_in_time || DEFAULT_CHECK_IN;
  const reservationDisplayCheckOut = reservationData?.check_out_time || DEFAULT_CHECK_OUT;

  const dateRange = useMemo(() => {
    if (!reservationData?.start_date || !reservationData?.end_date) return null;
    return { start: parseISO(reservationData.start_date), end: parseISO(reservationData.end_date) };
  }, [reservationData?.start_date, reservationData?.end_date]);

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (!dateRange) return false;
      return !isWithinInterval(date, { start: dateRange.start, end: dateRange.end });
    },
    [dateRange]
  );

  const addChecklistAssignment = useCallback(() => {
    setChecklistAssignments((prev) => [...prev, { checklistId: defaultChecklistId ?? "", assigneeValue: "unassigned" }]);
  }, [defaultChecklistId]);

  const removeChecklistAssignment = useCallback((index: number) => {
    setChecklistAssignments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateChecklistAssignment = useCallback((index: number, patch: { checklistId?: string; assigneeValue?: string }) => {
    setChecklistAssignments((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  }, []);

  const isStartedTask = Boolean(
    mode === "edit" && task && (task.started_at || task.status === "in_progress")
  );

  /** True when edit form assignee differs from the saved task (pass-along candidate). */
  const assigneeChangedFromSaved = useMemo(() => {
    if (mode !== "edit" || !task) return false;
    const t = task as CleaningTaskWithRelations & {
      assignee_user_id?: string | null;
      assignmentRows?: Array<{
        checklist_id: string;
        assigned_type: string;
        cleaner_id: string | null;
        assignee_user_id: string | null;
      }>;
    };

    if (checklistAssignments.length > 0) {
      const saved =
        t.assignmentRows && t.assignmentRows.length > 0
          ? t.assignmentRows.map((row) => {
              let av = "unassigned";
              if (row.assigned_type === "owner" && user?.id === row.assignee_user_id) av = "self";
              else if (row.assignee_user_id) av = USER_PREFIX + row.assignee_user_id;
              else if (row.cleaner_id) av = row.cleaner_id;
              return `${row.checklist_id}:${av}`;
            })
          : task.checklist_id
            ? [
                (() => {
                  let av = "unassigned";
                  if (t.assigned_type === "owner" || (t.assignee_user_id && user?.id === t.assignee_user_id))
                    av = "self";
                  else if (t.assignee_user_id) av = USER_PREFIX + t.assignee_user_id;
                  else if (task.cleaner_id) av = task.cleaner_id;
                  return `${task.checklist_id}:${av}`;
                })(),
              ]
            : [];
      const next = checklistAssignments.map((row) => `${row.checklistId}:${row.assigneeValue || "unassigned"}`);
      if (saved.length !== next.length) return true;
      return saved.some((s, i) => s !== next[i]);
    }

    let savedAv = "unassigned";
    if (t.assigned_type === "owner" || (t.assignee_user_id && user?.id === t.assignee_user_id)) savedAv = "self";
    else if (t.assignee_user_id) savedAv = USER_PREFIX + t.assignee_user_id;
    else if (task.cleaner_id) savedAv = task.cleaner_id;
    return (assigneeValue || "unassigned") !== savedAv;
  }, [mode, task, checklistAssignments, assigneeValue, user?.id]);

  return {
    task,
    property,
    isLoading: mode === "edit" && taskLoading,
    assignableMembers,
    teamMembers,
    coordinatorOptions,
    cleanerMembers,
    coordinatorValue,
    setCoordinatorValue,
    propertyChecklists,
    defaultChecklistId,
    reservationData,
    reservationDisplayCheckIn,
    reservationDisplayCheckOut,
    assigneeValue,
    setAssigneeValue,
    checklistId,
    setChecklistId,
    checklistAssignments,
    setChecklistAssignments,
    addChecklistAssignment,
    removeChecklistAssignment,
    updateChecklistAssignment,
    includeInventoryCheck,
    setIncludeInventoryCheck,
    scheduledDate,
    setScheduledDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    status,
    setStatus,
    notes,
    setNotes,
    overrideCheckInTime,
    setOverrideCheckInTime,
    overrideCheckOutTime,
    setOverrideCheckOutTime,
    guestCount,
    setGuestCount,
    save,
    remove,
    isSaving,
    isDeleting,
    dateRange,
    isDateDisabled,
    isStartedTask,
    assigneeChangedFromSaved,
    companyName: companyAssignment?.company_id
      ? (allCompanies ?? []).find((c) => c.id === companyAssignment.company_id)?.company_name ?? null
      : null,
  };
}

function parseTimeTo24(t: string): string | null {
  const trimmed = (t || "").trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!match) return null;
  let h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  const period = (match[3] || "").toLowerCase();
  if (period === "pm" && h < 12) h += 12;
  if (period === "am" && h === 12) h = 0;
  if (!period && h <= 23) return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}
